import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, fileUrl } from "../lib/api";
import { useAuth } from "../auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Copy, Search, ExternalLink, Share2, DollarSign } from "lucide-react";
import OxyHeader from "../components/OxyHeader";

export default function AffiliateProducts() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState({ affiliate: null, products: [] });
  const [q, setQ] = useState("");

  useEffect(() => { if (!user) nav("/affiliate/signup"); }, [user]);
  useEffect(() => {
    if (user) api.get("/affiliate/products").then(r => setData(r.data)).catch(() => toast.error("Failed to load"));
  }, [user]);

  const copy = (link) => {
    const full = `${window.location.origin}${link}`;
    navigator.clipboard.writeText(full);
    toast.success("Link copied!");
  };
  const share = async (p) => {
    const full = `${window.location.origin}${p.affiliate_link}`;
    if (navigator.share) {
      try { await navigator.share({ title: p.name, text: p.name, url: full }); } catch {}
    } else copy(p.affiliate_link);
  };

  const products = data.products.filter(p => !q || p.name.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="min-h-screen bg-slate-50">
      <OxyHeader/>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
          <div>
            <div className="text-xs font-bold uppercase text-amber-600">Affiliate</div>
            <h1 className="font-display text-3xl font-black">All product links</h1>
            <p className="font-bn text-slate-500 text-sm mt-1">প্রতিটি প্রোডাক্টের আপনার এফিলিয়েট লিংক শেয়ার করুন।</p>
          </div>
          {data.affiliate && (
            <div className="bg-white rounded-xl border p-3 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-amber-400 to-rose-500 flex items-center justify-center text-white font-bold">{data.affiliate.name?.[0] || "A"}</div>
              <div>
                <div className="text-xs text-slate-500">Your code</div>
                <div className="font-mono font-bold text-slate-900">{data.affiliate.code}</div>
              </div>
              <Link to="/affiliate"><Button size="sm" variant="outline" data-testid="aff-dash-link"><DollarSign className="w-4 h-4 mr-1"/>Dashboard</Button></Link>
            </div>
          )}
        </div>

        <div className="mb-4 relative max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
          <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search products…" className="pl-9" data-testid="aff-search"/>
        </div>

        {products.length === 0 ? (
          <div className="bg-white rounded-xl border p-10 text-center text-slate-500">No products available yet. Check back soon!</div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {products.map(p => (
              <div key={p.product_id} className="bg-white rounded-xl border overflow-hidden flex flex-col hover:shadow-lg transition" data-testid={`aff-product-${p.product_id}`}>
                <div className="aspect-square bg-slate-100 bg-cover bg-center relative" style={{backgroundImage: p.images?.[0] ? `url(${fileUrl(p.images[0])})` : "none"}}>
                  {p.discount_price && p.discount_price < p.price && (
                    <div className="absolute top-2 left-2 bg-rose-500 text-white text-[10px] font-black px-2 py-0.5 rounded">SALE</div>
                  )}
                </div>
                <div className="p-3 flex-1 flex flex-col">
                  <div className="font-bn font-semibold text-sm line-clamp-2 min-h-[2.5rem]">{p.name}</div>
                  <div className="text-[10px] text-slate-500 mt-0.5">{p.store_name}</div>
                  <div className="mt-2 flex items-baseline gap-1">
                    <span className="font-black text-rose-600">৳{p.discount_price || p.price}</span>
                    {p.discount_price && <span className="text-xs text-slate-400 line-through">৳{p.price}</span>}
                  </div>
                  <div className="mt-3 flex gap-1">
                    <Button size="sm" onClick={() => copy(p.affiliate_link)} className="flex-1 h-8 bg-slate-900 hover:bg-slate-800 text-xs" data-testid={`aff-copy-${p.product_id}`}><Copy className="w-3 h-3 mr-1"/>Copy</Button>
                    <Button size="sm" variant="outline" onClick={() => share(p)} className="h-8"><Share2 className="w-3 h-3"/></Button>
                    <a href={p.affiliate_link} target="_blank" rel="noreferrer"><Button size="sm" variant="outline" className="h-8"><ExternalLink className="w-3 h-3"/></Button></a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
